import React, { useState } from "react";
import ChannelIcon from "./ChannelIcon";
import Tooltip from "./Tooltip";
import InfoIcon from "./InfoIcon";

const TONE_BG = {
  urgent:        'bg-[#FFE5E5]',
  assertive:     'bg-[#FFF0D9]',
  formal:        'bg-[#F0FFD6]',
  informal:      'bg-[#D6FFF0]',
  technical:     'bg-[#D6F9FF]',
  collaborative: 'bg-[#E0EDFF]',
  creative:      'bg-[#EDE0FF]',
  casual:        'bg-[#FFE0EF]',
};

const ChannelToneTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'channel',
    direction: 'asc'
  });

  const sortedData = [...data].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderDominantTone = (tones) => {
    if (!tones || tones.length === 0) return <span className="text-grey-60">No data</span>;

    // tones array is sorted desc by value, first item is dominant
    const dominant = tones[0];
    const bgColor = TONE_BG[dominant.tone] || 'bg-grey-20';

    return (
      <span className={`px-2 py-0.5 rounded-md text-sm ${bgColor} text-black-0`}>
        {dominant.text} {dominant.value}%
      </span>
    );
  };

  return (
    <div>
      <h3 className="text-md font-normal text-black-0 px-10 py-3">Channel tone</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-20">
          <thead>
            <tr>
              <th 
                onClick={() => requestSort('channel')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                Channel {getSortDirectionIndicator('channel')}
              </th>
              <th 
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60"
              >
                <span className="inline-flex items-center gap-1">
                  Dominant tone
                  <Tooltip content="Most prevalent tone inferred from messages in the channel." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-20">
            {sortedData.map((channel) => (
              <tr key={channel.id} className="hover:bg-white-f">
                <td className="pl-10 pr-5 py-3 text-sm font-medium text-black-0">
                  <div className="flex items-center gap-1">
                    <ChannelIcon isPrivate={channel.is_private} size={14} />
                    <span>{channel.channel}</span>
                  </div>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm">
                  {renderDominantTone(channel.frequentTones)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelToneTable;
